import * as React from 'react';
import { overlayAnimation, panelAnimation, panelAnimationStyle } from '@wayste/shadcn/lib/animations';
import { cn } from '@wayste/shadcn/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { type LucideIcon, XIcon } from 'lucide-react';
import { ActionFooter, type ActionFooterAction } from './action-footer';
import { Button } from './button';
import { Toolbar, ToolbarTitle } from './toolbar';

/**
 * Modal Component
 *
 * A centered overlay panel. Built on top of Radix UI Dialog primitive.
 * Follows the same Toolbar + Body + ActionFooter pattern as Drawer.
 *
 * @example
 * ```tsx
 * import { Modal, ModalTrigger, ModalContent, ModalToolbar, ModalBody, ModalFooter } from '@wayste/shadcn';
 *
 * function MyComponent() {
 *   return (
 *     <Modal>
 *       <ModalTrigger asChild>
 *         <Button>Open Modal</Button>
 *       </ModalTrigger>
 *       <ModalContent width="md">
 *         <ModalToolbar label="Create order" />
 *         <ModalBody className="p-5">
 *           Your content goes here
 *         </ModalBody>
 *         <ModalFooter
 *           primaryAction={{ label: 'Save', onClick: handleSave }}
 *           secondaryAction={{ label: 'Cancel', onClick: () => setOpen(false) }}
 *         />
 *       </ModalContent>
 *     </Modal>
 *   );
 * }
 * ```
 */

// ─── Width ────────────────────────────────────────────────────────────────────

export type ModalWidth = 'sm' | 'md' | 'lg' | 'full';

const modalWidthMap: Record<Exclude<ModalWidth, 'full'>, string> = {
    sm: 'w-[400px]',
    md: 'w-[560px]',
    lg: 'w-[720px]',
};

// ─── Context ──────────────────────────────────────────────────────────────────

// Lets ModalToolbar and ModalFooter automatically adapt to full-screen mode
// without requiring the caller to pass extra props.
const ModalFullscreenContext = React.createContext(false);

// ─── Primitives ───────────────────────────────────────────────────────────────

/** Root modal component that manages open/closed state. */
const Modal = DialogPrimitive.Root;

/** Trigger element that opens the modal when clicked. Use `asChild` to compose with your own button. */
const ModalTrigger = DialogPrimitive.Trigger;

/** Element that closes the modal when clicked. Use `asChild` to compose with your own button. */
const ModalClose = DialogPrimitive.Close;

const ModalPortal = DialogPrimitive.Portal;

const ModalOverlay = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Overlay>) => (
    <DialogPrimitive.Overlay ref={ref} className={cn(overlayAnimation, 'fixed inset-0 z-[350] bg-black/50', className)} {...props} />
);
ModalOverlay.displayName = 'ModalOverlay';

// ─── ModalContent ─────────────────────────────────────────────────────────────

interface ModalContentProps extends React.ComponentPropsWithRef<typeof DialogPrimitive.Content> {
    /**
     * Width of the modal panel.
     * - sm  — 400px (default)
     * - md  — 560px
     * - lg  — 720px
     * - full — covers the entire viewport; toolbar and footer use larger padding
     * @default 'sm'
     */
    width?: ModalWidth;
    /**
     * When true, enables overflow-hidden for use with ModalBody scrollable content.
     * @default false
     */
    scrollable?: boolean;
}

/**
 * Main content container for the modal.
 *
 * @param width - 'sm' (400px) | 'md' (560px) | 'lg' (720px) | 'full' (fills viewport)
 * @param scrollable - When true, applies overflow-hidden for use with ModalBody
 *
 * @example
 * ```tsx
 * <ModalContent width="md">
 *   <ModalToolbar label="Title" />
 *   <ModalBody className="p-5">Content</ModalBody>
 *   <ModalFooter primaryAction={{ label: 'Save', onClick: handleSave }} />
 * </ModalContent>
 * ```
 */
const ModalContent = ({ width = 'sm', className, children, scrollable = false, ref, ...props }: ModalContentProps) => {
    const isFullscreen = width === 'full';

    const positionClass = isFullscreen
        ? 'fixed inset-0'
        : cn('fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh]', modalWidthMap[width]);

    // Non-fullscreen modals always need overflow-hidden to clip the toolbar and
    // footer edges to the rounded corners (Figma "clip content"). Fullscreen
    // only needs it when scrollable=true to enable ModalBody scrolling.
    const shapeClass = isFullscreen
        ? ''
        : 'rounded-2xl overflow-hidden border border-border-default shadow-[0px_4px_12px_2px_rgba(0,0,0,0.12)]';

    return (
        <ModalPortal>
            <ModalOverlay />
            <DialogPrimitive.Content
                ref={ref}
                className={cn(
                    panelAnimation,
                    'bg-surface-tertiary z-[350] flex flex-col',
                    positionClass,
                    shapeClass,
                    isFullscreen && scrollable ? 'overflow-hidden' : '',
                    className,
                )}
                style={!isFullscreen ? panelAnimationStyle : undefined}
                {...props}
            >
                <ModalFullscreenContext.Provider value={isFullscreen}>{children}</ModalFullscreenContext.Provider>
            </DialogPrimitive.Content>
        </ModalPortal>
    );
};
ModalContent.displayName = 'ModalContent';

// ─── ModalToolbar ─────────────────────────────────────────────────────────────

interface ModalToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Label text displayed in the toolbar. */
    label: string;
    /**
     * Whether to show the close (X) button on the right.
     * @default true
     */
    showCloseButton?: boolean;
    /** Optional Lucide icon rendered to the left of the title text. */
    icon?: LucideIcon;
    /**
     * Extra action buttons rendered between the title and the close button.
     * Use for additional icon-button actions (e.g. delete, share).
     * @example
     * ```tsx
     * actions={
     *   <Button variant="ghost" size="icon-sm" aria-label="Delete">
     *     <Trash2 className="size-4" />
     *   </Button>
     * }
     * ```
     */
    actions?: React.ReactNode;
}

/**
 * Toolbar header for the modal. Automatically uses size="lg" padding when
 * the parent ModalContent has width="full".
 *
 * @example
 * ```tsx
 * <ModalToolbar label="Create order" />
 * <ModalToolbar label="Delete Item" icon={TrashIcon} />
 * ```
 */
const ModalToolbar = ({ label, showCloseButton = true, icon, actions, className, ...props }: ModalToolbarProps) => {
    const isFullscreen = React.useContext(ModalFullscreenContext);
    return (
        <Toolbar size={isFullscreen ? 'lg' : 'default'} className={className} {...(props as object)}>
            <ToolbarTitle icon={icon}>{label}</ToolbarTitle>
            {actions}
            {showCloseButton && (
                <ModalClose asChild>
                    <Button variant="ghost" size="icon-sm" aria-label="Close">
                        <XIcon className="size-4" />
                        <span className="sr-only">Close</span>
                    </Button>
                </ModalClose>
            )}
        </Toolbar>
    );
};
ModalToolbar.displayName = 'ModalToolbar';

// ─── ModalBody ────────────────────────────────────────────────────────────────

/**
 * Scrollable body section for the modal, sits between ModalToolbar and ModalFooter.
 * Automatically handles flex layout and overflow scrolling.
 *
 * @example
 * ```tsx
 * <ModalContent scrollable>
 *   <ModalToolbar label="Title" />
 *   <ModalBody className="p-5">
 *     Content goes here
 *   </ModalBody>
 *   <ModalFooter primaryAction={{ label: 'Save', onClick: handleSave }} />
 * </ModalContent>
 * ```
 */
const ModalBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-1 flex-col overflow-y-auto', className)} {...props} />
);
ModalBody.displayName = 'ModalBody';

// ─── ModalFooter ──────────────────────────────────────────────────────────────

interface ModalFooterProps {
    /** Primary action — renders on the far right as a filled primary button. */
    primaryAction?: ActionFooterAction;
    /** Secondary action — renders to the left of the primary button. */
    secondaryAction?: ActionFooterAction;
    /** Tertiary action — renders on the far left as an outlined button. */
    tertiaryAction?: ActionFooterAction;
    className?: string;
    children?: React.ReactNode;
}

/**
 * Footer for the modal, backed by ActionFooter. Automatically uses size="lg"
 * padding when the parent ModalContent has width="full". Always anchored to
 * the bottom of the modal.
 *
 * To close the modal on button click, call your `onOpenChange(false)` inside
 * the action's `onClick` handler, or wrap the footer in a ModalClose.
 *
 * @example
 * ```tsx
 * <ModalFooter
 *   primaryAction={{ label: 'Save', onClick: handleSave, disabled: !isValid }}
 *   secondaryAction={{ label: 'Cancel', onClick: () => setOpen(false) }}
 *   tertiaryAction={{ label: 'Delete', onClick: handleDelete }}
 * />
 * ```
 */
const ModalFooter = ({ className, children, primaryAction, secondaryAction, tertiaryAction }: ModalFooterProps) => {
    const isFullscreen = React.useContext(ModalFullscreenContext);
    return (
        <ActionFooter
            noBorder
            size={isFullscreen ? 'lg' : 'default'}
            className={cn('border-border-subtle mt-auto border-t', className)}
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
            tertiaryAction={tertiaryAction}
        >
            {children}
        </ActionFooter>
    );
};
ModalFooter.displayName = 'ModalFooter';

// ─── Accessibility helpers ────────────────────────────────────────────────────

/**
 * Accessible title for the modal. Required by screen readers.
 * Can be visually hidden with className="sr-only" if ModalToolbar already
 * provides the visible title.
 */
const ModalTitle = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Title>) => (
    <DialogPrimitive.Title ref={ref} className={cn('text-text-primary text-base font-semibold', className)} {...props} />
);
ModalTitle.displayName = 'ModalTitle';

/**
 * Accessible description for the modal. Optional but recommended for
 * providing additional context to screen readers.
 */
const ModalDescription = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Description>) => (
    <DialogPrimitive.Description ref={ref} className={cn('text-text-secondary text-sm', className)} {...props} />
);
ModalDescription.displayName = 'ModalDescription';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
    Modal,
    ModalTrigger,
    ModalClose,
    ModalPortal,
    ModalOverlay,
    ModalContent,
    ModalToolbar,
    ModalBody,
    ModalFooter,
    ModalTitle,
    ModalDescription,
};

// Re-export ToolbarTitle and ActionFooter for single-import convenience
export { ToolbarTitle, ActionFooter };
